import { createContext, useCallback, useContext, useRef, useState } from 'react'
import styles from './Toast.module.css'

export type ToastVariant = 'info' | 'success' | 'warning' | 'danger'

interface ToastItem { id: number; message: string; variant: ToastVariant }
interface ToastCtxType { toast: (message: string, variant?: ToastVariant) => void }

const ToastCtx = createContext<ToastCtxType | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const counter = useRef(0)

  const toast = useCallback((message: string, variant: ToastVariant = 'info') => {
    const id = ++counter.current
    setItems(prev => [...prev, { id, message, variant }])
    setTimeout(() => setItems(prev => prev.filter(t => t.id !== id)), 3500)
  }, [])

  return (
    <ToastCtx.Provider value={{ toast }}>
      {children}
      <div className={styles.region} aria-live="polite" aria-label="Notifications">
        {items.map(t => (
          <div key={t.id} className={`${styles.toast} ${styles[`toast-${t.variant}`]}`} role="status">
            <span className={styles.icon}>{variantIcon(t.variant)}</span>
            <span>{t.message}</span>
            <button
              className={styles.dismiss}
              aria-label="Dismiss notification"
              onClick={() => setItems(prev => prev.filter(x => x.id !== t.id))}
            >✕</button>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastCtx)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx.toast
}

function variantIcon(v: ToastVariant) {
  return { info: 'ℹ️', success: '✓', warning: '⚠', danger: '✕' }[v]
}
